import { useState, useEffect } from 'react';
import { Crown, Sparkles, CheckCircle, Star, Zap, Trophy, Gift, ArrowRight, X } from 'lucide-react';

export default function PremiumWelcomeModal({ isOpen, onClose, userName, packageName }) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      setStep(0);
      
      // Animate steps
      const timer1 = setTimeout(() => setStep(1), 500);
      const timer2 = setTimeout(() => setStep(2), 1000);
      const timer3 = setTimeout(() => setStep(3), 1500);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const benefits = [
    { icon: Zap, text: '2000+ Güncel Soru', color: 'text-yellow-400' },
    { icon: Trophy, text: 'Sınırsız Pratik Hakkı', color: 'text-green-400' },
    { icon: Star, text: 'Gerçek Sınav Deneyimi', color: 'text-purple-400' },
    { icon: Gift, text: 'Detaylı İstatistikler', color: 'text-blue-400' },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Confetti Effect */}
      {showConfetti && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{
                  backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][Math.floor(Math.random() * 6)],
                  transform: `rotate(${Math.random() * 360}deg)`,
                }}
              />
            </div>
          ))}
        </div>
      )}
      
      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-lg mx-4 transform animate-modal-pop">
        {/* Glow Effect */}
        <div className="absolute -inset-4 bg-gradient-to-r from-yellow-400 via-red-500 to-purple-600 rounded-3xl blur-xl opacity-50 animate-pulse" />
        
        {/* Main Card */}
        <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl border border-white/20 overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white/70" />
          </button>
          
          {/* Header with Crown Animation */}
          <div className="relative pt-8 pb-6 px-6 text-center">
            {/* Floating Stars */}
            <div className="absolute top-4 left-8 animate-bounce" style={{ animationDelay: '0.1s' }}>
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            </div>
            <div className="absolute top-8 right-12 animate-bounce" style={{ animationDelay: '0.3s' }}>
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div className="absolute top-12 left-16 animate-bounce" style={{ animationDelay: '0.5s' }}>
              <Star className="w-4 h-4 text-pink-400 fill-pink-400" />
            </div>
            
            {/* Crown Icon with Glow */}
            <div className="relative inline-block mb-4">
              <div className="absolute inset-0 bg-yellow-400 rounded-full blur-xl opacity-50 animate-pulse" />
              <div className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-500 p-6 rounded-full shadow-2xl transform animate-bounce-slow">
                <Crown className="w-16 h-16 text-white drop-shadow-lg" />
              </div>
            </div>
            
            {/* Welcome Text */}
            <h2 className="text-3xl md:text-4xl font-black mb-2">
              <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-orange-400 bg-clip-text text-transparent">
                Tebrikler! 🎉
              </span>
            </h2>
            <p className="text-xl text-white font-semibold mb-1">
              Hoş Geldin, <span className="text-yellow-400">{userName || 'Premium Üye'}</span>!
            </p>
            <p className="text-white/60 text-sm">
              {packageName || 'Premium'} paketiniz aktif edildi
            </p>
          </div>
          
          {/* Benefits Section */}
          <div className="px-6 pb-6">
            <div className="bg-white/5 rounded-2xl p-4 mb-6">
              <h3 className="text-sm font-bold text-white/80 mb-4 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Artık erişebileceğiniz özellikler:
              </h3>
              
              <div className="grid grid-cols-2 gap-3">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-2 transition-all duration-500 ${
                      step > index ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg bg-white/10`}>
                      <benefit.icon className={`w-4 h-4 ${benefit.color}`} />
                    </div>
                    <span className="text-white text-sm font-medium">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* CTA Button */}
            <button
              onClick={onClose}
              className="w-full group relative overflow-hidden bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-white py-4 px-6 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] shadow-2xl"
            >
              {/* Shine Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
              
              <span className="relative flex items-center justify-center gap-2">
                <Zap className="w-5 h-5" />
                Premium'a Başla
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
            
            {/* Footer Note */}
            <p className="text-center text-white/40 text-xs mt-4">
              İsveç sürücü belgesi sınavına hazırlık yolculuğunuz başladı! 🚗
            </p>
          </div>
        </div>
      </div>
      
      {/* Custom Styles */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        @keyframes modal-pop {
          0% {
            transform: scale(0.8) translateY(20px);
            opacity: 0;
          }
          100% {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        
        .animate-confetti {
          animation: confetti linear forwards;
        }
        
        .animate-modal-pop {
          animation: modal-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
